import { Button, Card, NonIdealState, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { getFormattedCost } from "components/common/constraintFormattingHelpers";
import { getAllSizeLabels, getLabelStyle } from "components/common/labels";
import ConstraintDetailsDialogTable, {
    ConstraintTableConfig
} from "components/detailPane/BusDetailPane/ConstraintDetailsDialog/ConstraintsDetailsDialogTable";
import { getExportConstraintsToCSV } from "components/detailPane/BusDetailPane/ConstraintDetailsDialog/constraintsTableHelpers";
import {
    getAllocatedCost,
    getTotalCost,
    getTotalNumberConstraintsForSize
} from "components/detailPane/BusDetailPane/constraintsHelpers";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import {
    Constraint_Stacks_Bool_Exp,
    useGetQueueProjectHopsQuery,
    useGetQueueProjectScopeMembershipQuery,
    useGetRelatedQueueProjectsQuery
} from "generated/graphql";
import { loader } from "graphql.macro";
import { getHasuraDataAndConvertToNiraType } from "graphql/helpers/queryHelpers";
import React, {
    PropsWithChildren,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    Constraint,
    convertHasuraConstraintsToConstraints
} from "types/constraintType";
import { STORAGE_TYPE } from "types/generatorType";
import { Loading } from "types/loadingType";
import {
    buildNumHopsByProject,
    convertHasuraDataToProjectsByConstraint,
    RelatedProjectsByConstraint
} from "types/queueProjectRelatednessType";
import { RegionType } from "types/regionType";
import {
    isUserScopeInProgress,
    MISO_2022_REALISTIC_SCOPE_TYPE,
    Scope,
    UserScopeStatus,
    USER_SCOPE_STATUS_TO_DISPLAY_STRING
} from "types/scope";
import { getCapacityCostsScopeViewFromRegionAndGenerator } from "types/scopeViewType";
import "./BusResultsSection.scss";

const GET_CONSTRAINTS = loader("src/graphql/getConstraints.graphql");

/**
 * Wraps a wide table in a horizontal scroll container with soft edge
 * shadows that appear when there is more content in that direction — a
 * visible cue that columns are cut off.
 */
const HorizontalScrollTableWrapper: React.FC<PropsWithChildren> = ({
    children
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const element = scrollRef.current;
        if (element === null) {
            return;
        }
        setCanScrollLeft(element.scrollLeft > 0);
        // -1 tolerates fractional scroll widths on zoomed displays.
        setCanScrollRight(
            element.scrollLeft + element.clientWidth < element.scrollWidth - 1
        );
    }, []);

    useLayoutEffect(() => {
        updateScrollState();
        const element = scrollRef.current;
        if (element === null) {
            return;
        }
        // Re-evaluate when the container resizes or the table content
        // changes width (e.g. constraints finish loading).
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(element);
        if (element.firstElementChild !== null) {
            observer.observe(element.firstElementChild);
        }
        return () => observer.disconnect();
    }, [updateScrollState]);

    return (
        <div className="BusResultsSection-scroll-container">
            <div
                ref={scrollRef}
                className="BusResultsSection-table-wrapper"
                onScroll={updateScrollState}
            >
                {children}
            </div>
            {canScrollLeft && (
                <div className="BusResultsSection-scroll-shadow BusResultsSection-scroll-shadow-left" />
            )}
            {canScrollRight && (
                <div className="BusResultsSection-scroll-shadow BusResultsSection-scroll-shadow-right" />
            )}
        </div>
    );
};

const BusResultsSection: React.FC = () => {
    const { selectedScope } = useBusPageConfiguration();

    if (!selectedScope) {
        return null;
    }

    const userScopeStatus = selectedScope.userScopeConfig?.status;

    if (
        userScopeStatus !== undefined &&
        userScopeStatus !== UserScopeStatus.COMPLETED
    ) {
        const isRunning = isUserScopeInProgress(userScopeStatus);

        return (
            <div className="BusResultsSection">
                <NonIdealState
                    icon={isRunning ? <Spinner /> : "error"}
                    title={USER_SCOPE_STATUS_TO_DISPLAY_STRING[userScopeStatus]}
                    description={
                        isRunning
                            ? "Results are being computed. This page updates on its own when they are ready."
                            : "Something went wrong computing results for this scope. Please try re-studying the bus."
                    }
                />
            </div>
        );
    }

    return <BusResultsContent selectedScope={selectedScope} />;
};

// Split out from BusResultsSection so every hook below runs
// unconditionally for the life of the mounted component.
// BusResultsSection's early returns (no scope selected / scope not
// COMPLETED) happen before this component mounts; if they lived in the
// same component as these hooks, a scope-status transition would change
// the number of hooks between renders and crash (Rules of Hooks).
const BusResultsContent: React.FC<{ readonly selectedScope: Scope }> = ({
    selectedScope
}) => {
    const { bus, pricedConstraintConfig, selectedGeneratorType, powerAmounts } =
        useBusPageConfiguration();

    const scopeView = getCapacityCostsScopeViewFromRegionAndGenerator(
        bus.region,
        selectedGeneratorType
    );

    const constraintsWhereClause: Constraint_Stacks_Bool_Exp = {
        nira_bus_id: { _eq: bus.niraBusId },
        scope: { _eq: selectedScope.scope },
        scope_view: { _eq: scopeView }
    };

    const maybeConstraints: Loading<ReadonlyArray<Constraint>> =
        getHasuraDataAndConvertToNiraType(
            GET_CONSTRAINTS,
            convertHasuraConstraintsToConstraints,
            { constraintsWhereClause }
        );

    const isMisoBus = bus.region === RegionType.MISO;
    // Same query (and variables) as RelatedProjectsPanel in the sidebar —
    // Apollo dedupes the two into one request. Pinned to miso_2022_realistic
    // so the per-constraint expand data lines up with that panel.
    const relatednessQuery = useGetRelatedQueueProjectsQuery({
        variables: {
            nira_bus_id: bus.niraBusId,
            scope: MISO_2022_REALISTIC_SCOPE_TYPE,
            scope_view: scopeView,
            region: bus.region
        },
        skip: !isMisoBus
    });

    // Same queries (and variables) as RelatedProjectsPanel — Apollo
    // dedupes them, so enriching the expand panels with hops and scope
    // membership costs no extra network requests.
    const hopsQuery = useGetQueueProjectHopsQuery({
        variables: {
            nira_bus_id: bus.niraBusId,
            scope_view: scopeView,
            region: bus.region
        },
        skip: !isMisoBus
    });
    const membershipQuery = useGetQueueProjectScopeMembershipQuery({
        variables: {
            region: bus.region,
            current_scope: selectedScope.scope,
            realistic_scope: MISO_2022_REALISTIC_SCOPE_TYPE,
            scope_view: scopeView,
            include_miso_realistic: true
        },
        skip: !isMisoBus
    });
    const numHopsByProject: ReadonlyMap<string, number> | undefined =
        useMemo(() => {
            // Hops is a supplementary lookup: if the query errored (e.g. the
            // table isn't deployed in this environment) render the expand
            // panels without a hops column rather than blocking on it.
            if (hopsQuery.data === undefined || hopsQuery.error !== undefined) {
                return undefined;
            }
            return buildNumHopsByProject(hopsQuery.data);
        }, [hopsQuery.data, hopsQuery.error]);
    const inCurrentScopeProjectNumbers: ReadonlySet<string> | undefined =
        useMemo(() => {
            if (membershipQuery.data === undefined) {
                return undefined;
            }
            return new Set(
                membershipQuery.data.in_current_scope.map(
                    (row) => row.project_number
                )
            );
        }, [membershipQuery.data]);

    // Only surface per-constraint chevrons when the user is viewing the
    // same scope the relatedness query is pinned to (MISO Realistic).
    // On other scopes the constraint table and relatedness data wouldn't
    // line up cleanly, so we hide the chevron column entirely to avoid
    // partial/confusing matches.
    const isViewingRealisticScope =
        selectedScope.scope === MISO_2022_REALISTIC_SCOPE_TYPE;
    const relatedProjectsByConstraint: RelatedProjectsByConstraint | undefined =
        useMemo(() => {
            if (
                !isMisoBus ||
                !isViewingRealisticScope ||
                relatednessQuery.data === undefined
            ) {
                return undefined;
            }
            return convertHasuraDataToProjectsByConstraint(
                relatednessQuery.data,
                powerAmounts,
                numHopsByProject,
                inCurrentScopeProjectNumbers
            );
        }, [
            isMisoBus,
            isViewingRealisticScope,
            relatednessQuery.data,
            numHopsByProject,
            inCurrentScopeProjectNumbers,
            powerAmounts.energySize,
            powerAmounts.capacitySize,
            powerAmounts.chargingSize
        ]);

    const { hasAllocatedCost, showCapacity, showChargingSize } =
        pricedConstraintConfig;
    const shouldShowCharging =
        selectedGeneratorType === STORAGE_TYPE && showChargingSize;
    const labelStyle = getLabelStyle(
        pricedConstraintConfig,
        selectedGeneratorType
    );
    const sizeLabel = getAllSizeLabels(
        powerAmounts,
        showCapacity,
        shouldShowCharging,
        labelStyle
    );

    const totalCost = getTotalCost(maybeConstraints, powerAmounts);
    const allocatedCost = getAllocatedCost(maybeConstraints, powerAmounts);
    const numConstraints =
        maybeConstraints === "loading"
            ? "loading"
            : getTotalNumberConstraintsForSize(maybeConstraints, powerAmounts);

    // TODO: Consolidate all table configs into this config object and then create a helper
    // function to generate table config so we reduce the amount of copy paste everywhere
    const constraintTableConfig: ConstraintTableConfig = {
        hideAllocatedCosts: !hasAllocatedCost,
        hideTotalCosts: false,
        showPostStudyLoad: true,
        showContingency: pricedConstraintConfig.showContingency,
        showContingencyDetails: pricedConstraintConfig.showContingencyDetails,
        generatorType: selectedGeneratorType,
        missingBranchMetadata: pricedConstraintConfig.missingBranchMetadata,
        hideResourceType: pricedConstraintConfig.hideResourceType,
        showImpactPercent: pricedConstraintConfig.showImpactPercent
    };

    // The on-page table hides the detail columns (they render inside each
    // row's expand panel instead) so it fits the main column without
    // crushing. The CSV export below keeps the full column set.
    const compactConstraintTableConfig: ConstraintTableConfig = {
        ...constraintTableConfig,
        compactColumns: true
    };

    const exportConstraints = getExportConstraintsToCSV(
        bus.busDisplayName,
        maybeConstraints,
        {
            constraintTableConfig,
            labelStyle,
            powerAmounts
        }
    );

    const resultCards = (
        <div className="BusResultsSection-cards">
            {hasAllocatedCost && (
                <ResultCard
                    label="Allocated Cost"
                    value={allocatedCost}
                    formatter={getFormattedCost}
                />
            )}
            <ResultCard
                label="Total Cost"
                value={totalCost}
                formatter={getFormattedCost}
            />
            <ResultCard
                label="# of Constraints"
                value={numConstraints}
                formatter={(n) => n.toString()}
            />
        </div>
    );

    return (
        <div className="BusResultsSection">
            <h3 className="bp5-heading BusResultsSection-heading">
                Study Results @ {sizeLabel}
            </h3>
            <div className="BusResultsSection-cards-row">
                {resultCards}
                <Button
                    size="small"
                    variant="minimal"
                    icon={IconNames.DOWNLOAD}
                    onClick={exportConstraints}
                    disabled={maybeConstraints === "loading"}
                >
                    Download CSV
                </Button>
            </div>
            <HorizontalScrollTableWrapper>
                <ConstraintDetailsDialogTable
                    constraints={maybeConstraints}
                    powerAmounts={powerAmounts}
                    showAllConstraints={false}
                    showCapacityLabel={showCapacity}
                    showChargingLabel={shouldShowCharging}
                    labelStyle={labelStyle}
                    constraintTableConfig={compactConstraintTableConfig}
                    relatedProjectsByConstraint={relatedProjectsByConstraint}
                    striped={false}
                    rowBorders
                />
            </HorizontalScrollTableWrapper>
        </div>
    );
};

const ResultCard: React.FC<{
    readonly label: string;
    readonly value: Loading<number>;
    readonly formatter: (value: number) => string;
}> = ({ label, value, formatter }) => {
    return (
        <Card className="BusResultsSection-card">
            <div className="bp5-text-disabled">{label}</div>
            <h3 className="bp5-heading BusResultsSection-card-value">
                {value === "loading" ? <Spinner size={20} /> : formatter(value)}
            </h3>
        </Card>
    );
};

export default BusResultsSection;
